import React from 'react';
import { useIntl } from 'react-intl-next';
import { ACTION, ACTION_SUBJECT, ACTION_SUBJECT_ID, EVENT_TYPE, INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import { mediaInsertMessages } from '@atlaskit/editor-common/messages';
import { Popup, withOuterListeners } from '@atlaskit/editor-common/ui';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import { akEditorFloatingDialogZIndex } from '@atlaskit/editor-shared-styles';
import { Box } from '@atlaskit/primitives';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { useFocusEditor } from '../hooks/use-focus-editor';
import { useUnholyAutofocus } from '../hooks/use-unholy-autofocus';
import { LocalMedia } from './LocalMedia';
import { MediaFromURL } from './MediaFromURL';
import { MediaInsertWrapper } from './MediaInsertWrapper';
var PopupWithListeners = withOuterListeners(Popup);
var getDomRefFromSelection = function getDomRefFromSelection(view, dispatchAnalyticsEvent) {
  try {
    var domRef = findDomRefAtPos(view.state.selection.from, view.domAtPos.bind(view));
    if (domRef instanceof HTMLElement) {
      return domRef;
    } else {
      throw new Error('Invalid DOM reference');
    }
  } catch (error) {
    if (dispatchAnalyticsEvent) {
      var payload = {
        action: ACTION.ERRORED,
        actionSubject: ACTION_SUBJECT.PICKER,
        actionSubjectId: ACTION_SUBJECT_ID.PICKER_MEDIA,
        eventType: EVENT_TYPE.OPERATIONAL,
        attributes: {
          error: 'Error getting DOM reference from selection'
        }
      };
      dispatchAnalyticsEvent(payload);
    }
  }
};
export var MediaInsertPicker = function MediaInsertPicker(_ref) {
  var _useSharedPluginState, _useSharedPluginState2;
  var api = _ref.api,
    editorView = _ref.editorView,
    dispatchAnalyticsEvent = _ref.dispatchAnalyticsEvent,
    popupsMountPoint = _ref.popupsMountPoint,
    popupsBoundariesElement = _ref.popupsBoundariesElement,
    popupsScrollableElement = _ref.popupsScrollableElement,
    _closeMediaInsertPicker = _ref.closeMediaInsertPicker,
    insertMediaSingle = _ref.insertMediaSingle,
    insertExternalMediaSingle = _ref.insertExternalMediaSingle;
  var targetRef = getDomRefFromSelection(editorView, dispatchAnalyticsEvent);
  var isOpen = (_useSharedPluginState = useSharedPluginState(api, ['mediaInsert'])) === null || _useSharedPluginState === void 0 || (_useSharedPluginState = _useSharedPluginState.mediaInsertState) === null || _useSharedPluginState === void 0 ? void 0 : _useSharedPluginState.isOpen;
  var mediaProvider = (_useSharedPluginState2 = useSharedPluginState(api, ['media'])) === null || _useSharedPluginState2 === void 0 || (_useSharedPluginState2 = _useSharedPluginState2.mediaState) === null || _useSharedPluginState2 === void 0 ? void 0 : _useSharedPluginState2.mediaProvider;
  var intl = useIntl();
  var focusEditor = useFocusEditor({
    editorView: editorView
  });
  var _useUnholyAutofocus = useUnholyAutofocus(),
    autofocusRef = _useUnholyAutofocus.autofocusRef,
    onPositionCalculated = _useUnholyAutofocus.onPositionCalculated;
  if (!isOpen || !mediaProvider) {
    return null;
  }
  var handleClose = function handleClose(exitMethod) {
    return function (event) {
      event.preventDefault();
      if (dispatchAnalyticsEvent) {
        var payload = {
          action: ACTION.CLOSED,
          actionSubject: ACTION_SUBJECT.PICKER,
          actionSubjectId: ACTION_SUBJECT_ID.PICKER_MEDIA,
          eventType: EVENT_TYPE.UI,
          attributes: {
            exitMethod: exitMethod
          }
        };
        dispatchAnalyticsEvent(payload);
      }
      _closeMediaInsertPicker();
      focusEditor();
    };
  };
  return /*#__PURE__*/React.createElement(PopupWithListeners, {
    ariaLabel: intl.formatMessage(mediaInsertMessages.mediaPickerPopupAriaLabel),
    offset: [0, 12],
    target: targetRef,
    zIndex: akEditorFloatingDialogZIndex,
    fitHeight: 390,
    fitWidth: 340,
    mountTo: popupsMountPoint,
    boundariesElement: popupsBoundariesElement,
    handleClickOutside: handleClose(INPUT_METHOD.MOUSE),
    handleEscapeKeydown: handleClose(INPUT_METHOD.KEYBOARD),
    scrollableElement: popupsScrollableElement,
    preventOverflow: true,
    onPositionCalculated: onPositionCalculated,
    focusTrap: true
  }, /*#__PURE__*/React.createElement(MediaInsertWrapper, null, /*#__PURE__*/React.createElement(Tabs, {
    id: "media-insert-tab-navigation"
  }, /*#__PURE__*/React.createElement(Box, {
    paddingBlockEnd: "space.150"
  }, /*#__PURE__*/React.createElement(TabList, null, /*#__PURE__*/React.createElement(Tab, null, intl.formatMessage(mediaInsertMessages.fileTabTitle)), /*#__PURE__*/React.createElement(Tab, null, intl.formatMessage(mediaInsertMessages.linkTabTitle)))), /*#__PURE__*/React.createElement(TabPanel, null, /*#__PURE__*/React.createElement(LocalMedia, {
    ref: autofocusRef,
    mediaProvider: mediaProvider,
    insertMediaSingle: insertMediaSingle,
    closeMediaInsertPicker: function closeMediaInsertPicker() {
      _closeMediaInsertPicker();
      focusEditor();
    },
    dispatchAnalyticsEvent: dispatchAnalyticsEvent
  })), /*#__PURE__*/React.createElement(TabPanel, null, /*#__PURE__*/React.createElement(MediaFromURL, {
    mediaProvider: mediaProvider,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    closeMediaInsertPicker: function closeMediaInsertPicker() {
      _closeMediaInsertPicker();
      focusEditor();
    },
    insertMediaSingle: insertMediaSingle,
    insertExternalMediaSingle: insertExternalMediaSingle
  })))));
};